import axios, { CancelTokenSource } from 'axios';
import { BillConsumerRenderProps, withBillContext } from 'components/bill/BillContext';
import { getBillIcon } from 'components/bill/utils/billUtils';
import HomeTableHeaderCell from 'components/layout/HomeTableHeaderCell';
import LoaderComponent from 'components/LoaderComponent';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Icon, Table } from 'semantic-ui-react';
import { getAllBillRemindersByBillId } from 'services/billReminderServices';
import { getAllBillsByTreatmentId } from 'services/billServices';
import styled from 'styled-components';
import { BillDto, BillReminderDto, TreatmentDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const BillDetailsInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  max-height: 224px;

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }
`;

const StyledRemindersTable = styled(Table)`

  &.ui.table thead tr th:first-child {
    padding-left: 14px !important;
   }

  &.ui.table tbody tr td:first-child {
    padding-left: 14px !important;
  }
`;

const StyledTableCellWithIcons = styled(Table.Cell)`
  padding-right: 0 !important;
  text-align: center !important;
`;

const Asterisk = styled(Icon)`
  &.small.icon {
    margin-right: 0.05rem;
    font-size: .7em;
  }
`;

const StyledTableRemindersCell = styled(Table.Cell)`
  padding-left: 10px !important;
  padding-right: 10px !important;
`;

interface Props extends MessageSourceProps, BillConsumerRenderProps {
  selectedTreatment?: TreatmentDto
}

interface State {
  selectedBill?: BillDto,
  billReminders?: Array<BillReminderDto>,
  remindersVisible: boolean,
  loadingInProgress: boolean,
  cancelTokenSource: CancelTokenSource
}

class BillDetailsInfo extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      selectedBill: undefined,
      billReminders: [],
      remindersVisible: false,
      loadingInProgress: false,
      cancelTokenSource
    }
  }

  getBillDetails(selectedTreatment: TreatmentDto | undefined): void {
    const {cancelTokenSource} = this.state;

    if (!this.mounted) {
      return;
    }

    const onFinally = () => {
      this.setState({
        loadingInProgress: false
      })
    };

    const {setBills, setSelectedBill} = this.props;
    if (selectedTreatment) {
      this.setState({
        loadingInProgress: true
      });
      getAllBillsByTreatmentId(selectedTreatment.id, cancelTokenSource)
          .then(response => {
            const selectedTreatmentProps = this.props.selectedTreatment;
            if (!selectedTreatmentProps || selectedTreatment.id === selectedTreatmentProps.id) {

              this.setState({
                selectedBill: response[0]
              });

              setBills(response);
              setSelectedBill(response[0])
            }
          })
          .catch(noop)
          .finally(onFinally);
    } else {
      this.setState({
        selectedBill: undefined
      });

      setBills([]);
    }
  }

  componentDidMount(): void {
    const {selectedTreatment} = this.props;
    this.mounted = true;

    if (selectedTreatment) {
      this.getBillDetails(selectedTreatment);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>, snapshot?: any): void {
    const {selectedTreatment} = this.props;

    if (prevProps.selectedTreatment != selectedTreatment) {
      this.getBillDetails(selectedTreatment);
    }
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  findBillById = (billId: number): BillDto | undefined => {

    const {bills} = this.props;

    return bills.find((bill: BillDto) => bill.id === billId);
  };

  showReminders = (billId: number) => {

    const foundBill = this.findBillById(billId);
    const {cancelTokenSource} = this.state;

    this.setState((previousState: State) => {
      if (previousState.selectedBill != foundBill) {
        return {
          remindersVisible: true
        }
      } else {
        return {
          remindersVisible: !previousState.remindersVisible,
          billReminders: []
        }
      }
    });

    if (foundBill && foundBill.hasReminders) {
      this.setState({
        billReminders: []
      });
      getAllBillRemindersByBillId(billId, cancelTokenSource).then(response => {
        this.setState(() => ({
          billReminders: response
        }));
      }).catch(noop);
    }
  };

  markBill = (billId: number) => {
    const foundBill = this.findBillById(billId);

    this.setState((previousState: State) => {
      if (previousState.selectedBill != foundBill) {
        return {
          selectedBill: foundBill
        }
      } else {
        return {
          selectedBill: previousState.selectedBill
        }
      }
    });
  };

  billDetailsInfoTable(): React.ReactNode {

    const {bills, getMessage} = this.props;
    const {selectedBill, billReminders, remindersVisible} = this.state;

    const selectedBillId = selectedBill ? selectedBill.id : -1;

    const numberFormat = new Intl.NumberFormat("en-US", {minimumFractionDigits: 2});
    return (

        <BillDetailsInfoContainer>
          <Table basic='very' celled padded selectable compact>
            <Table.Header>
              <Table.Row>
                <HomeTableHeaderCell>{getMessage('bill')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('bill.date')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>#</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('value')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('bill.paymentDate')}</HomeTableHeaderCell>
                <HomeTableHeaderCell>{getMessage('bill.openAmount')}</HomeTableHeaderCell>
              </Table.Row>
            </Table.Header>
            {bills && (
                <Table.Body>
                  {
                    bills.map(bill => {

                      let totalBillReminders: JSX.Element[] = [];
                      if (bill.totalBillReminders != null) {
                        totalBillReminders = Array(bill.totalBillReminders).fill((<div></div>)).map(
                            (_, i) => (<Asterisk key={i} name='asterisk' size="small"/>));
                      }

                      let billRemindersTable;

                      if (billReminders!.length > 0 && billReminders![0].billId == bill.id && remindersVisible) {
                        billRemindersTable = (
                            <Table.Row>
                              <StyledTableRemindersCell colSpan={6}>
                                <StyledRemindersTable>
                                  <Table.Header>
                                    <Table.Row>
                                      <HomeTableHeaderCell>Reminder Date</HomeTableHeaderCell>
                                      <HomeTableHeaderCell>Next Date</HomeTableHeaderCell>
                                      <HomeTableHeaderCell>Amount</HomeTableHeaderCell>
                                    </Table.Row>
                                  </Table.Header>
                                  <Table.Body>
                                    {
                                      billReminders!.map(br => (
                                          <Table.Row key={br.id}>
                                            <Table.Cell>{br.reminderDate}</Table.Cell>
                                            <Table.Cell>{br.nextDate}</Table.Cell>
                                            <Table.Cell>{br.amount}</Table.Cell>
                                          </Table.Row>
                                      ))
                                    }
                                  </Table.Body>
                                </StyledRemindersTable>
                              </StyledTableRemindersCell>
                            </Table.Row>
                        );
                      }

                      const billRow = (
                          <React.Fragment key={bill.id}>
                            <Table.Row
                                key={bill.id}
                                active={bill.id == selectedBillId}
                                onClick={() => this.markBill(bill.id)}
                            >
                              <StyledTableCellWithIcons
                                  onClick={() => selectedBill!.hasReminders ? this.showReminders(bill.id) : noop}
                              >
                                {getBillIcon(bill)}
                              </StyledTableCellWithIcons>
                              <Table.Cell>{bill.billDate}</Table.Cell>
                              <Table.Cell>{`${bill.id}`} <sup>{totalBillReminders}</sup></Table.Cell>
                              <Table.Cell textAlign="right">{numberFormat.format(bill.amount)}</Table.Cell>
                              <Table.Cell>{bill.paymentDate}</Table.Cell>
                              <Table.Cell textAlign="right"
                                          style={{paddingRight: "40px"}}>{numberFormat.format(bill.openAmount)}</Table.Cell>
                            </Table.Row>
                            {billRemindersTable}
                          </React.Fragment>
                      );
                      return (
                          billRow
                      );
                    })
                  }
                </Table.Body>
            )}
          </Table>
        </BillDetailsInfoContainer>
    );

  }

  render(): React.ReactNode {
    const {loadingInProgress} = this.state;
    const {getMessage} = this.props;

    if (loadingInProgress) {
      return (
          <LoaderComponent message={getMessage("billDetails.loader")}/>
      );
    }
    return (
        this.billDetailsInfoTable()
    );
  }
}

export default withMessages(withBillContext(BillDetailsInfo));
